<template>
  <div class="table-container">
    <div class="table-controls-list d-flex justify-content-between align-items-center mb-4">
      <UsersStateListComponent
          class="mx-4"
          :table-name="uiTableName"
          :members-list="members.map(member => member.user)"
          @changeActive="changeActive($event)"
        />
      <div class="d-flex align-items-center ml-4" v-if="!isDataLoading && managers.length">
        <DatePeriodNavigation
          :start-date="dates.start"
          :end-date="dates.end"
          :disable-prev="isDataLoading"
          :disable-next="disabledBtn === 0 || isDataLoading"
          :disabled-datepickers="true"
          :disabled-start-dates="disabledFirstDate"
          :disabled-end-dates="disabledSecondDate"
          date-format="dd/MM/yy"
          :monday-first="true"
          @changePeriod="changeWeek"
          @selectDate="selectDates"
        />
      </div>
    </div>

    <div class="table-responsive-wrapper" v-if="!isDataLoading && managers.length && getMembers.length">
      <b-table-simple
        class="table__theme-default table__hours-tracking position-relative"
        :class="{'w-75': getMembers.length > 2 && getMembers.length <= 4, 'w-50': getMembers.length <= 2}"
        :fixed="false"
        stripped
        responsive
      >
        <colgroup><col></colgroup>
        <colgroup><col></colgroup>
        <template v-for="(member, index) in getMembers">
          <colgroup class="group-border-left" :style="index === 0 ? 'border-left: none;' : ''" :key="`${member.user.name}`"><col class="wh-col"><col class="lh-col"><col class="ch-col"></colgroup>
        </template>

        <b-thead head-variant="light">
          <b-tr class="group-border group-border-header">
            <b-th class="text-left first-sticky managers-column columnheader" rowspan="2" sticky-column>Managers</b-th>
            <b-th class="second-sticky border-x columnheader text-right" rowspan="2" sticky-column>Logged hours</b-th>
            <template v-for="(member, i) in getMembers">
              <b-th class="text-uppercase text-center member-name" :key="`${member.user.name}-${i}`" colspan="3" v-if="userState[member.user.id]">
                <div class="member-group-header">
                  <TableUserItem
                    class="text-center m-auto justify-content-center"
                    :user="member.user"
                    :item-max-width="'180px'"
                    view="name-only"
                  />
                  <div class="member-group-divider"></div>
                </div>
              </b-th>
            </template>
          </b-tr>
          <b-tr class="group-border group-border-header">
            <template v-for="member in getMembers">
              <b-th class="wh-col columnheader text-right" :key="`${member.user.name}-wh`" v-if="userState[member.user.id]">
                Work hours
              </b-th>
              <b-th class="lh-col columnheader text-right" :key="`${member.user.name}-lh`" v-if="userState[member.user.id]">
                Logged hours
              </b-th>
              <b-th class="ch-col columnheader text-right" :key="`${member.user.name}-ch`" v-if="userState[member.user.id]">
                Commercial hours
              </b-th>
            </template>
          </b-tr>
        </b-thead>

        <b-tbody>
          <b-tr v-for="(manager, i) in getManagers" :key="manager.name">
            <b-td class="first-sticky" sticky-column>
              <span class="truncated-text" v-b-tooltip.hover="{ variant: 'base', placement: 'top', placement:'top', boundary:'viewport' }" :title="manager.name">{{ manager.name }}</span>
            </b-td>
            <b-td class="second-sticky border-x text-right" sticky-column>{{ manager.logged_hours }}</b-td>
            <template v-for="member in manager.members">
              <b-td
                v-if="i === 0 && userState[member.id]"
                class="wh-col text-right"
                :key="`${member.name}-wh-${manager.name}`"
                :rowspan="managers.length"
              >
                {{ getMemberByName(member.name).work_hours }}
              </b-td>
              <b-td
                v-if="userState[member.id]"
                class="lh-col text-right"
                :key="`${member.name}-lh-${manager.name}`"
              >
                {{ member.logged_hours }}
              </b-td>
              <b-td
                v-if="userState[member.id]"
                :key="`${member.name}-ch-${manager.name}`"
                class="cursor-pointer position-relative variable-td ch-col text-right"
                @dblclick="showChange(manager.id, member.id, member.commercial_hours)"
              >
                <template v-if="!tdChange[`change-${manager.id}-${member.id}`]">
                  <template v-if="member.commercial_hours != member.logged_hours && +member.commercial_hours !== 0">
                    <span :class="member.commercial_hours != member.logged_hours && +member.commercial_hours !== 0 ? 'tag tag--light-error' : ''">
                      <span class="tag__text">{{ member.commercial_hours }}</span>
                    </span>
                    <span class="material-icons cursor-pointer ml-1 info-icon">info_outline</span>
                  </template>
                  <template v-else>
                    <span class="val">{{ member.commercial_hours }}</span>
                  </template>
                </template>
                <div v-else class="d-flex justify-content-around align-items-center">
                  <span class="material-icons cancel" @click="hideChange(manager.id, member.id)">close</span>
                  <b-form-input
                    class="input-change"
                    v-model="member.commercial_hours"
                    maxlength="5"
                    autofocus
                    @keyup.enter="saveChange(manager.id, member.id, member.commercial_hours)"
                    @keyup.esc="hideChange(manager.id, member.id)"
                  />
                  <span class="material-icons done" @click="saveChange(manager.id, member.id, member.commercial_hours)">done</span>
                </div>
              </b-td>
            </template>
          </b-tr>
        </b-tbody>

        <b-tfoot class="tfoot">
          <b-tr>
            <b-td class="first-sticky" sticky-column>Total</b-td>
            <b-td class="second-sticky border-x text-right" sticky-column>{{ getTotal | round(2) }}</b-td>
            <template v-for="member in getTotalData">
              <b-td
                v-if="userState[member.id]"
                class="wh-col text-right"
                :key="`${member.id}-total-wh`"
                :rowspan="managers.length"
              >
                {{ member.total_wh | round(2) }}
              </b-td>
              <b-td
                v-if="userState[member.id]"
                class="d-flex align-items-center lh-col justify-content-end"
                :key="`${member.id}-total-lh`"
              >
                <template v-if="member.total_lh < member.total_wh">
                  <span :class="member.total_lh < member.total_wh ? 'tag tag--light-error' : ''">
                    <span class="tag__text">{{ member.total_lh | round(2) }}</span>
                  </span>
                  <span class="material-icons cursor-pointer ml-2 info-icon" @click="getUserInfo(member)">info_outline</span>
                </template>
                <template v-else>
                  {{ member.total_lh | round(2) }}
                </template>
                &#8203;<!-- zero-width space to trigger line-height -->
              </b-td>
              <b-td
                v-if="userState[member.id]"
                :key="`${member.id}-total-ch`"
                class="position-relative ch-col text-right"
              >
                <template v-if="member.total_ch !== member.total_lh && +member.total_ch !== 0">
                  <span :class="member.total_ch !== member.total_lh && +member.total_ch !== 0 ? 'tag tag--light-error' : ''">
                    <span class="tag__text">{{ member.total_ch | round(2) }}</span>
                  </span>
                  <span class="material-icons cursor-pointer ml-1 info-icon">info_outline</span>
                </template>
                <template v-else>
                  <span>{{ member.total_ch | round(2) }}</span>
                </template>
              </b-td>
            </template>
          </b-tr>
          <!-- Empty row to provide space for scrollbar -->
          <b-tr class="scrollbar-spacer-row">
            <b-td class="first-sticky" sticky-column>&nbsp;</b-td>
            <b-td class="second-sticky" sticky-column>&nbsp;</b-td>
            <template v-for="member in getTotalData">
              <b-td v-if="userState[member.id]" :key="`${member.id}-spacer-wh`" class="wh-col">&nbsp;</b-td>
              <b-td v-if="userState[member.id]" :key="`${member.id}-spacer-lh`" class="lh-col">&nbsp;</b-td>
              <b-td v-if="userState[member.id]" :key="`${member.id}-spacer-ch`" class="ch-col">&nbsp;</b-td>
            </template>
          </b-tr>
        </b-tfoot>
      </b-table-simple>
    </div>

    <div class="preloader" v-if="isDataLoading">
      <div class="spinner-border spinner-theme_default text-primary" role="status">
        <span class="sr-only">Loading...</span>
      </div>
    </div>
    <div v-if="!isDataLoading && (!managers.length || (managers.length && !getMembers.length))" class="empty-state-container" style="min-height: 300px; display: flex; align-items: center; justify-content: center;">
      <EmptyStateNoResults
        title="No hours tracking report yet"
        description="No hours tracked for the selected period. Time entries from all employees will appear here once recorded."
        customIcon="event_available"
        customIconClasses="material-symbols-outlined empty-state__icon"
        :withoutButton="true"
      />
    </div>
  </div>
</template>

<script>
import { cloneDeep } from 'lodash'
import { mapGetters } from 'vuex'
import Moment from 'moment'
import UsersStateListComponent from '@components/UsersStateListComponent'
import EmptyStateNoResults from '@components/EmptyStateNoResults.vue'
import TableUserItem from '@components/tables/TableUserItem'
import DatePeriodNavigation from '@components/DatePeriodNavigation.vue'

export default {
  components: {
    UsersStateListComponent,
    EmptyStateNoResults,
    TableUserItem,
    DatePeriodNavigation
  },
  props: {
    currentWeek: {
      type: Object,
      default: () => ({})
    },
    managers: {
      type: Array,
      default: () => ([])
    },
    members: {
      type: Array,
      default: () => ([])
    },
    totalData: {
      type: Array,
      default: () => ([])
    },
    disabledBtn: {
      type: Number,
      default: 0
    },
    isDataLoading: {
      type: Boolean,
      default: false
    },
    showIncorrectLoggedHoursOnly: {
      type: Boolean,
      default: false
    },
    showIncorrectCommercialHoursOnly: {
      type: Boolean,
      default: false
    }
  },
  watch: {
    disabledBtn (v) {
      this.disabledBtn = v
    },
    currentWeek: {
      handler (v) {
        this.dates.start = v.start
        this.dates.end = v.end
      },
      deep: true
    }
  },
  data () {
    return {
      userState: {},
      allActiveUsers: 0,
      uiTableName: 'hours_tracking_report',
      tdChange: {},
      dates: {
        start: null,
        end: null
      },
      dateChanged: false,
      tempValue: null
    }
  },
  mounted () {
    this.dates = cloneDeep(this.currentWeek)
  },
  computed: {
    ...mapGetters({
      dateFormat: 'dateFormat'
    }),
    getManagers () {
      return this.managers
    },
    getMembers () {
      return this.members
    },
    getTotalData () {
      return this.totalData
    },
    getTotal () {
      return this.getSum(this.managers, 'logged_hours')
    },
    disabledFirstDate () {
      return {
        from: this.dates.end > new Date() ? new Date() : this.dates.end
      }
    },
    disabledSecondDate () {
      return {
        from: this.dates.end > new Date() ? this.dates.end : new Date(),
        ranges: [
          {
            from: new Date(2000, 1, 1),
            to: this.dates.start
          }
        ]
      }
    }
  },
  methods: {
    getMemberByName (name) {
      return this.members.find(member => member.user.name === name)
    },
    getSum (arr, key) {
      let sum = 0
      for (let i in arr) {
        sum += arr[i][key]
      }
      return sum
    },
    changeActive (disUsers) {
      const members = this.members
      for (const i in members) {
        if (disUsers.some(id => members[i].user.id === id)) {
          this.userState[members[i].user.id] = false
        } else {
          this.userState[members[i].user.id] = true
        }
      }
      this.allActiveUsers = Object.values(this.userState).filter(item => !!item).length
      this.$forceUpdate()
    },
    showChange (managerId, memberId, val) {
      this.tdChange = {}
      this.tdChange[`change-${managerId}-${memberId}`] = true
      this.tempValue = val

      this.$forceUpdate()
    },
    hideChange (managerId, memberId) {
      const curManager = this.managers.find(manager => manager.id === managerId)
      const curMember = curManager.members.find(member => member.id === memberId)

      curMember.commercial_hours = this.tempValue

      this.tdChange = {}
      this.tempValue = null
      this.$forceUpdate()
    },
    saveChange (managerId, memberId, hours) {
      this.axios
        .post('/api/finances/commercial-hours/save-to-hours-tracking-report', {
          date_yw: this.currentWeek.date_yw,
          manager_id: managerId,
          member_id: memberId,
          hours
        }, {
          headers: {
            Authorization: 'Bearer' + localStorage.getItem('access_token')
          }
        })
        .then(({ data }) => {
          this.$snotify.success(
            'Commercial hours successfully saved',
            'Success',
            this.$store.state.snotify
          )
        })
        .catch(error => {
          this.errorNotifications(error)
        })

      const managerIndex = this.managers.findIndex(manager => manager.id === managerId)
      const memberIndex = this.managers[managerIndex].members.findIndex(member => member.id === memberId)
      this.managers[managerIndex].members[memberIndex].commercial_hours = hours

      this.$emit('dataChanged')
      this.tdChange = {}
    },
    changeWeek (week) {
      this.$emit('changeWeek', week)
      this.dateChanged = false
    },
    selectDates (v, key) {
      this.dates[key] = v
      this.dateChanged = true
    },
    getUserInfo (member) {
      const dateFrom = Moment(this.dates.start).format('YYYY-MM-DD')
      const dateTo = Moment(this.dates.end).format('YYYY-MM-DD')

      const user = this.getMembers.find(userObj => userObj.user.id === member.id).user.jira_user_name

      this.$emit('getUserInfo', { users: [user], date_from: dateFrom, date_to: dateTo })
    }
  }
}
</script>

<style lang="scss" scoped>
.material-icons {
    width: 14px;
    height: 14px;
    font-size: 13px;
}

// Base table styles
.table__theme-default {
  table {
    td {
      padding: 12px 4px;
      line-height: 24px;
    }

    tr td {
      line-height: 24px;
    }
  }
}

// Button styles
.button-checkbox_sm {
  background-color: transparent;
  border: transparent;

  &:hover,
  &:focus {
    background-color: transparent;
    border: transparent;
    outline: none;
    box-shadow: none;
  }
}

// Main container
.table {
  &-container {
    background-color: $bgColorWhite;
    padding: 10px;
    border-radius: 10px;
  }

  &__hours-tracking {
    &.table__theme-default table tbody tr {
      border-bottom: none;
    }

    .thead-light th {
      background-color: $bgColorBase;
    }

    .member-name {
      text-align: center !important;
    }
  }
}

// Border utilities
.border-x {
  border-left: 1px solid $tableBorderColor;
}

.group-border {
  &-left {
    border-left: 1px solid $tableBorderColor;
  }

  &-right {
    border-right: 1px solid $tableBorderColor;
  }
}

// Header styles - unified approach
.columnheader {
  padding: 5px 4px !important;
  line-height: 14px;
  text-transform: capitalize;
  min-width: 61px;
}

.member-name {
  text-transform: none;
  max-width: 200px;
  min-width: 200px;
  padding: 4px 4px 0 4px !important;

  .member-group-header {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
  }

  .member-group-title {
    color: $fontColorBase;
    font-size: 11px;
    line-height: 20px;
  }

  .member-group-divider {
    width: 100%;
    height: 1px;
    background: $bgColor2;
  }

  ::v-deep .user-name {
    max-width: 140px;
    margin: 0;
    text-transform: capitalize !important;
  }

  ::v-deep .user-position {
    max-width: 120px;
  }
}

// Column width utilities - unified
.managers-column {
  min-width: 150px;
}

.wh-col,
.lh-col {
  min-width: 61px;
  line-height: 14px;
  text-transform: lowercase;
}

.ch-col {
    padding: 5px 4px !important;
    line-height: 14px;
    text-transform: lowercase;
}

// Sticky columns - unified
.first-sticky {
  max-width: 150px;
  background-color: $bgColorWhite;

  .truncated-text {
    display: block;
    max-width: 142px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

.second-sticky {
  left: 150px !important;
  background-color: $bgColorWhite;

  &.border-x,
  &.border-x.b-table-sticky-column {
    border-left: none !important;
    border-right: none !important;
    position: relative;

    &::before {
      content: '';
      position: absolute;
      left: 0;
      top: 0;
      bottom: 0;
      width: 1px;
      background-color: $tableBorderColor;
      z-index: 1;
    }

    &::after {
      content: '';
      position: absolute;
      right: 0;
      top: 0;
      bottom: 0;
      width: 1px;
      background-color: $tableBorderColor;
      z-index: 1;
    }
  }

  &.border-right,
  &.border-right.b-table-sticky-column {
    border-right: none !important;
    position: relative;

    &::after {
      content: '';
      position: absolute;
      right: 0;
      top: 0;
      bottom: 0;
      width: 1px;
      background-color: $tableBorderColor;
      z-index: 1;
    }
  }
}

// Interactive elements
.input-change {
  min-height: 20px;
  max-width: 40px;
  margin: auto;
  padding: 0;
  text-align: center;
  line-height: 1;
}

.material-icons {
  &.done {
    color: $fontColorSuccess;
  }

  &.cancel {
    color: $danger;
  }
}

.info-icon {
  width: 14px;
  height: 14px;
  font-size: 14px;
}

.variable-td {
  overflow: hidden;

  .val {
    font-weight: bold;
    text-decoration: underline;
  }
}

// Footer styles
.tfoot {
  border-top: 1px solid $bgColor2;
  font-weight: bold;

  tr:first-child {
    border-bottom: 1px solid $bgColor2;
  }

  tr:last-child td {
    border-bottom: none;
  }
}

// Error tag styles
.tag {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0 4px;
  border-radius: 3px;

  &__text {
    line-height: 20px;
    color: $fontColorBase;
    font-size: 13px;
  }

  &--light-error {
    background: $notifyErrBgColor;
  }
}

// Scrollbar wrapper
.table-responsive-wrapper {
  width: 100%;
  overflow-x: auto;
  overflow-y: visible;

  // Custom scrollbar styling
  &::-webkit-scrollbar {
    height: 8px;
    background: transparent;
  }

  &::-webkit-scrollbar-track {
    background: $bgColor2;
    border-radius: 25px;
    margin: 0 18px;
  }

  &::-webkit-scrollbar-thumb {
    background: $inputBorderColor2;
    border-radius: 25px;

    &:hover {
      background: #B0B0C8;
    }
  }

  // Firefox scrollbar
  scrollbar-width: thin;
  scrollbar-color: $inputBorderColor2 $bgColor2;
}

// Spacer row for scrollbar positioning
.scrollbar-spacer-row {
  height: 20px;

  td {
    height: 20px;
    border: none !important;
    background: transparent !important;
    padding: 0 !important;
    line-height: 14px;
    font-size: 1px;

    // Remove all borders
    border-left: none !important;
    border-right: none !important;
    border-top: none !important;
    border-bottom: none !important;

    &.wh-col {
      border-left: 1px solid white !important;
    }
  }
}
</style>
